import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { card, h1, h2, sub, btn, RGB, Icon, ICONS, errMsg } from '../ext/extUi';
import TeamStage from '../ext/stage/TeamStage';
import { S, TEAM_TINT } from '../ext/stage/StageArt';

// A panel team's DEFAULT stage seating (seen from the crowd) — the same screen as the external
// tournaments' team seating. Every new match of the team starts from it; seating changed later for a
// match never changes it. Until it is saved, the team's first 5 main players are used.
// Route: /admin/teams/:id/seating

export default function TeamSeatingPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [team, setTeam] = useState(null);
  const [seats, setSeats] = useState([]);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const apply = (data) => {
    const s = data.seats.map(x => x.steam_id);
    setTeam(data);
    setSeats(data.players.length >= 5 ? [...s, null, null, null, null, null].slice(0, 5) : s);
    setSaved(data.custom ? s : []);
  };

  useEffect(() => {
    axios.get(`/api/v1/teams/${id}/seating`).then(r => apply(r.data))
      .catch(e => setMsg({ error: true, text: errMsg(e, t('ext.loadFailed', 'Failed to load')) }));
  }, [id, t]);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await axios.put(`/api/v1/teams/${id}/seating`, { seats });
      apply(r.data);
      setMsg({ text: t('extSeating.saved', 'Seating saved') });
    } catch (e) { setMsg({ error: true, text: errMsg(e, t('ext.saveFailed', 'Could not save')) }); }
    finally { setBusy(false); }
  };

  if (!team) return <div style={sub}>{msg ? msg.text : t('common.loading', 'Loading...')}</div>;
  const enough = team.players.length >= 5;
  const complete = seats.length === 5 && seats.every(Boolean) && new Set(seats).size === 5;
  const dirty = JSON.stringify(seats) !== JSON.stringify(saved);

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '1.25rem', paddingBottom: '0.75rem' }}>
        <Link to="/admin/teams" style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><Icon d={ICONS.back} /> {t('seating.backToTeams', 'Back to the teams')}</Link>
        <h1 style={{ ...h1, marginTop: '0.35rem' }}>{t('extSeating.teamTitle', 'Stage seating · {{team}}', { team: team.name })}</h1>
      </div>
      <div style={card}>
        <h2 style={h2}>{t('extSeating.teamDefault', 'Default seating')}</h2>
        <p style={sub}>{t('extSeating.teamDefaultHint', 'Seen from the crowd: seat 1 is on the crowd’s left. This is the starting point for every match of this team; seating changed for a single match never changes it. The stage display boards read it from the API.')}</p>
        {!enough
          ? <p style={{ ...sub, color: 'var(--accent-amber)' }}>{t('seating.teamNeedsFive', 'This team has {{count}} player(s). Stage seating needs 5 players on the team.', { count: team.players.length })}</p>
          : (
            <>
              {!team.custom && <p style={{ ...sub, color: 'var(--accent-amber)' }}>{t('seating.notSavedYet', 'Not saved yet: new matches seat the team’s first 5 main players in this order.')}</p>}
              <div style={{ background: S.bg, border: `1px solid ${S.line}`, borderRadius: 12, padding: '0.4rem' }}>
                <TeamStage teamName={team.name} tint={TEAM_TINT.team1} players={team.players} seats={seats} onChange={setSeats} />
              </div>
            </>
          )}
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', justifyContent: 'flex-end', marginTop: '0.9rem', flexWrap: 'wrap' }}>
          {msg && <span style={{ fontSize: '0.8rem', color: msg.error ? 'var(--accent-red)' : 'var(--accent-green)', marginRight: 'auto' }}>{msg.text}</span>}
          {team.custom && dirty && <button type="button" style={btn(RGB.gray)} onClick={() => setSeats(saved)}>{t('extSeating.discard', 'Discard changes')}</button>}
          {enough && <button type="button" disabled={busy || !complete || !dirty} style={{ ...btn(RGB.green, true), opacity: complete && dirty ? 1 : 0.5 }} onClick={save}>{busy ? t('common.loading', 'Loading...') : t('extSeating.saveSeating', 'Save seating')}</button>}
          <Link to="/admin/teams" style={{ ...btn(RGB.blue), textDecoration: 'none' }}>{t('extSeating.done', 'Done')}</Link>
        </div>
      </div>
    </div>
  );
}
